import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { getAdminCenterData } from '@routes/sidebar/___shared___/model/adminCenterData.slice.ts';
import type { CommonTablePaginatedResponse } from '@routes/sidebar/admin-center/___shared___/types.ts';
import type {
    CreateEntityLocationRequest,
    EntityLocation,
    EntityLocationRequest,
    EntityLocationResponse,
    UpdateEntityLocationRequest,
} from '@routes/sidebar/admin-center/tenant-client/___pages___/configuration/___pages___/references/api/entity-location.api.ts';
import {
    createApiEntityLocation,
    deleteApiEntityLocation,
    getApiEntityLocation,
    updateApiEntityLocation,
} from '@routes/sidebar/admin-center/tenant-client/___pages___/configuration/___pages___/references/api/entity-location.api.ts';
import { extractError } from '@utils/slice.ts';

export interface EntityLocationError {
    message?: string;
    [k: string]: any;
}

export interface EntityLocationState {
    error: EntityLocationError | null;

    commonEntityLocation: EntityLocationResponse | null;
    entityLocations: EntityLocation[];
    pagination: CommonTablePaginatedResponse;

    updatedEntityLocation: EntityLocation | null;
    currentGetRequestId: string | null;

    isGetEntityLocationPending: boolean;
    isCreateEntityLocationPending: boolean;
    isUpdateEntityLocationPending: boolean;
    isDeleteEntityLocationPending: boolean;
}

const initialState: EntityLocationState = {
    error: null,

    commonEntityLocation: null,
    entityLocations: [],
    pagination: {
        pageNumber: 0,
        pageSize: 0,
        totalPages: 0,
        totalResults: 0,
        maxPageSize: 0,
    },

    updatedEntityLocation: null,
    currentGetRequestId: null,

    isGetEntityLocationPending: false,
    isCreateEntityLocationPending: false,
    isUpdateEntityLocationPending: false,
    isDeleteEntityLocationPending: false,
};

export const getEntityLocation = createAsyncThunk<
    EntityLocationResponse,
    { clientId: number; tenantAlias: string; payload: EntityLocationRequest },
    { rejectValue: EntityLocationError }
>('adminCenterEntityLocation/getEntityLocation', ({ clientId, tenantAlias, payload }, { rejectWithValue }) =>
    getApiEntityLocation(clientId, tenantAlias, payload)
        .then((res) => res)
        .catch((err) => rejectWithValue(err)),
);

export const createEntityLocation = createAsyncThunk<
    EntityLocation,
    { clientId: number; tenantAlias: string; payload: CreateEntityLocationRequest },
    { rejectValue: EntityLocationError }
>('adminCenterEntityLocation/createEntityLocation', ({ clientId, tenantAlias, payload }, { rejectWithValue }) =>
    createApiEntityLocation(clientId, tenantAlias, payload)
        .then((res) => res)
        .catch((err) => rejectWithValue(err)),
);

export const updateEntityLocation = createAsyncThunk<
    EntityLocation,
    { clientId: number; tenantAlias: string; id: number; payload: UpdateEntityLocationRequest },
    { rejectValue: EntityLocationError }
>('adminCenterEntityLocation/updateEntityLocation', ({ clientId, tenantAlias, id, payload }, { rejectWithValue }) =>
    updateApiEntityLocation(clientId, tenantAlias, id, payload)
        .then((res) => res)
        .catch((err) => rejectWithValue(err)),
);

export const deleteEntityLocation = createAsyncThunk<
    EntityLocation,
    { clientId: number; tenantAlias: string; id: number },
    { rejectValue: EntityLocationError }
>('adminCenterEntityLocation/deleteEntityLocation', ({ clientId, tenantAlias, id }, { rejectWithValue }) =>
    deleteApiEntityLocation(clientId, tenantAlias, id)
        .then((res) => res)
        .catch((err) => rejectWithValue(err)),
);

const entityLocationSlice = createSlice({
    name: 'adminCenterEntityLocation',
    initialState,
    reducers: {
        resetEntityLocationState: () => initialState,
    },
    extraReducers: (builder) => {
        builder.addCase(getEntityLocation.pending, (state, action) => {
            state.isGetEntityLocationPending = true;
            state.error = null;
            state.currentGetRequestId = action.meta.requestId;
        });
        builder.addCase(getEntityLocation.fulfilled, (state, action) => {
            if (state.currentGetRequestId !== action.meta.requestId) return;

            state.isGetEntityLocationPending = false;
            state.currentGetRequestId = null;
            const { data, pagination } = action.payload;
            state.commonEntityLocation = action.payload;
            state.entityLocations = data ?? [];
            state.pagination = pagination;
        });
        builder.addCase(getEntityLocation.rejected, (state, action) => {
            if (state.currentGetRequestId !== action.meta.requestId) return;

            state.isGetEntityLocationPending = false;
            state.currentGetRequestId = null;
            state.error = extractError(action);
        });

        builder.addCase(createEntityLocation.pending, (state) => {
            state.isCreateEntityLocationPending = true;
            state.error = null;
        });
        builder.addCase(createEntityLocation.fulfilled, (state, action) => {
            state.isCreateEntityLocationPending = false;
            state.updatedEntityLocation = action.payload;
        });
        builder.addCase(createEntityLocation.rejected, (state, action) => {
            state.isCreateEntityLocationPending = false;
            state.error = extractError(action);
        });

        builder.addCase(updateEntityLocation.pending, (state) => {
            state.isUpdateEntityLocationPending = true;
            state.error = null;
        });
        builder.addCase(updateEntityLocation.fulfilled, (state, action) => {
            state.isUpdateEntityLocationPending = false;
            state.updatedEntityLocation = action.payload;
        });
        builder.addCase(updateEntityLocation.rejected, (state, action) => {
            state.isUpdateEntityLocationPending = false;
            state.error = extractError(action);
        });

        builder.addCase(deleteEntityLocation.pending, (state) => {
            state.isDeleteEntityLocationPending = true;
            state.error = null;
        });
        builder.addCase(deleteEntityLocation.fulfilled, (state, action) => {
            state.isDeleteEntityLocationPending = false;
            state.updatedEntityLocation = action.payload;
            state.entityLocations = state.entityLocations.filter(
                (entityLocation) => entityLocation.id !== action.payload.id,
            );
        });
        builder.addCase(deleteEntityLocation.rejected, (state, action) => {
            state.isDeleteEntityLocationPending = false;
            state.error = extractError(action);
        });
        builder.addCase(getAdminCenterData.fulfilled, (state, action) => {
            const { clientTenantEntityLocationResponse } = action.payload;

            if (!clientTenantEntityLocationResponse) return;

            const { data, pagination } = clientTenantEntityLocationResponse;
            state.commonEntityLocation = clientTenantEntityLocationResponse;
            state.entityLocations = data ?? [];
            state.pagination = pagination;
        });
    },
});

export const { resetEntityLocationState } = entityLocationSlice.actions;
export default entityLocationSlice.reducer;
